import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'
import type { TreeNode } from './protectee'

/** Settings → Protectee (settings/Protectee::*): profilele de backup ale firmei. */
export type ProtecteeSettingsTab = { id: string; label: string; icon: string; href: string; right: string }
export type ProtecteeSettingsShell = { pageTitle: string; moduleUrl: string; tabs: ProtecteeSettingsTab[] }

export const TABS = ['backup_profiles', 'restore_tests'] as const
export type ProtecteeSettingsTabId = (typeof TABS)[number]

export function isProtecteeSettingsTab(value: string): value is ProtecteeSettingsTabId {
  return (TABS as readonly string[]).includes(value)
}

export type ProfileRow = {
  DT_RowId: string
  ID: number
  name: string
  destination: string
  destination_id: number
  path: string
  created: string
  devices: number
}

export type ProfileSheet = {
  profile: { ID: number; name: string; company_id: number; destination_id: number; path: string; user: string; has_password: boolean; destination_key: string; created: string }
  editable: boolean
  algorithm: Record<string, Record<string, string>>
  tree: TreeNode[]
  destinations: { ID: number; name: string }[]
  days_of_week: Record<string, string>
  months: Record<string, string>
  order_types: Record<string, string>
}

export type ProfileDevices = {
  profile: number
  devices: { device_id: number; profile_id: number; device_name: string; logged_user: string }[]
  allDevices: { ID: number; Denumire: string }[]
}

export type WriteAnswer = { ok?: boolean; message?: string; error?: string }

export function protecteeSettingsShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'protectee', 'shell'],
    queryFn: () => apiGet<ProtecteeSettingsShell>('/settings/protectee'),
  })
}

/** Cererea listei de profile, ca DataTables (`ProfilesTable.initDatatable`, serverSide): pagina, ordinea, cautarea. */
export type ProfilesRequest = { start: number; length: number; orderColumn: number; orderDir: 'asc' | 'desc'; search: string }

export function profilesTableQuery(r: ProfilesRequest) {
  const p = new URLSearchParams()
  p.set('draw', '1')
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  p.set('search[value]', r.search)
  return queryOptions({
    queryKey: ['settings', 'protectee', 'profiles', 'table', r],
    queryFn: () => apiGet<{ data: ProfileRow[]; recordsTotal: number; recordsFiltered: number }>('/settings/protectee/profiles/table?' + p.toString()),
  })
}

export function profileQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'protectee', 'profiles', id],
    queryFn: () => apiGet<ProfileSheet>('/settings/protectee/profiles/' + id),
  })
}

export function profileDevicesQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'protectee', 'profiles', id, 'devices'],
    queryFn: () => apiGet<ProfileDevices>('/settings/protectee/profiles/' + id + '/devices'),
  })
}

export function post(path: string, fields: Record<string, PostValue> = {}) {
  return apiPost<WriteAnswer>('/settings/protectee' + path, fields)
}

/** Settings > Protectee > Restore tests: calendarul testului de restaurare al firmei din sesiune. */
export type RestoreTestsPage = {
  ready: boolean
  periods: number[]
  company: { id: number; name: string } | null
  enabled: boolean
  period: number
  enabledAt: string | null
  last: { ticket_id: number; opened_at: string; done: boolean; files: number } | null
  canEdit: boolean
}

export function restoreTestsQuery() {
  return queryOptions({
    queryKey: ['settings', 'protectee', 'restore-tests'],
    queryFn: () => apiGet<RestoreTestsPage>('/settings/protectee/restore-tests'),
  })
}

export function saveRestoreTests(enabled: boolean, period: number) {
  return apiPost<RestoreTestsPage>('/settings/protectee/restore-tests', { enabled: enabled ? '1' : '0', period: String(period) })
}
